import * as z from 'zod';

import {
  BorrowerId,
  DelinquentInterestSettingId,
  EscrowCompanyId,
  EscrowSettingId,
  PaymentConfigId,
  PayoffId,
  PrincipalModificationId,
  QcDocumentId,
  WorkoutPlanId,
} from '../BrandedIds';
import {
  BorrowerUploadDocument,
  DefermentWorkoutPlanDocument,
  DelinquentInterestAppliedDocument,
  EscrowAnalysisDocument,
  FlexModCancellationNoticeDocument,
  ForbearanceWorkoutPlanCompletedDocument,
  ForbearanceWorkoutPlanEarlyTerminationNoticeDocument,
  Form1098Document,
  GoodbyeLetterDocument,
  LenderUploadDocument,
  LoanExtensionAgreementDocument,
  LoanExtensionFeesDocument,
  MortgageeLetterDocument,
  MortgageInsuranceCancellationNoticeDocument,
  PaidInFullStatementDocument,
  PartialPayoffConfirmationDocument,
  PaymentDeferralCancellationNoticeDocument,
  PaymentDeferralOfferLetterDocument,
  PayoffStatementDocument,
  PreActiveWelcomeLetterDocument,
  PrincipalModificationAppliedDocument,
  PrincipalModificationCancelledDocument,
  RepaymentPlanCancellationNoticeDocument,
  RepaymentPlanOfferNoticeDocument,
  VerificationOfMortgageDocument,
} from '../documents/Templates';
import { EmailTemplateType } from '../emails/Templates';
import { LoanDocumentForm1098Copy } from '../loan/Document';
import { BorrowerAccessSaveInput } from '../loan/DocumentAccess';
import { Form1098Year } from '../qcDocument/documents/Form1098';
import { zodBrandedUuid, zodDateOrString } from '../utils/Zod';
import { BaseLoanEvent } from './BaseLoanEvent';

const BaseSaveDocumentPdfEvent = BaseLoanEvent.extend({
  type: z.literal('saveDocumentPdf'),
  /**
   * NB the url field is NOT a URL but in fact the path of the document relative to the S3 bucket
   */
  url: z.string(),
  version: z.number(),
  comment: z.string().optional(),
  noteflowDocumentId: z.string().optional(),
});

/* ONE OFF DOCUMENT TEMPLATES */
export const PreActiveWelcomeLetterSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PreActiveWelcomeLetterDocument,
});
export type PreActiveWelcomeLetterSaveDocumentPdfEvent = z.infer<typeof PreActiveWelcomeLetterSaveDocumentPdfEvent>;

export const MortgageeLetterSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: MortgageeLetterDocument,
  escrowCompanyId: zodBrandedUuid<EscrowCompanyId>(),
});
export type MortgageeLetterSaveDocumentPdfEvent = z.infer<typeof MortgageeLetterSaveDocumentPdfEvent>;

const PayoffStatementSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PayoffStatementDocument,
  payoffId: zodBrandedUuid<PayoffId>(),
  borrowerNotifications: z.array(zodBrandedUuid<BorrowerId>()),
  isPartial: z.boolean().optional(),
});
export type PayoffStatementSaveDocumentPdfEvent = z.infer<typeof PayoffStatementSaveDocumentPdfEvent>;

const PaidInFullDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PaidInFullStatementDocument,
  payoffId: zodBrandedUuid<PayoffId>(),
});

const PartialPayoffConfirmationDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PartialPayoffConfirmationDocument,
  payoffId: zodBrandedUuid<PayoffId>(),
});

export const VerificationOfMortgageSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: VerificationOfMortgageDocument,
});
export type VerificationOfMortgageSaveDocumentPdfEvent = z.infer<typeof VerificationOfMortgageSaveDocumentPdfEvent>;

export const LoanExtensionAgreementSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: LoanExtensionAgreementDocument,
});
export type LoanExtensionAgreementSaveDocumentPdfEvent = z.infer<typeof LoanExtensionAgreementSaveDocumentPdfEvent>;

export const LoanExtensionFeesSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: LoanExtensionFeesDocument,
  borrowerNotifications: z.array(zodBrandedUuid<BorrowerId>()),
});
export type LoanExtensionFeesSaveDocumentPdfEvent = z.infer<typeof LoanExtensionFeesSaveDocumentPdfEvent>;

export const LenderUploadSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: LenderUploadDocument,
  title: z.string(),
  borrowerAccess: BorrowerAccessSaveInput,
});
export type LenderUploadSaveDocumentPdfEvent = z.infer<typeof LenderUploadSaveDocumentPdfEvent>;

export const BorrowerUploadSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: BorrowerUploadDocument,
  title: z.string(),
});
export type BorrowerUploadSaveDocumentPdfEvent = z.infer<typeof BorrowerUploadSaveDocumentPdfEvent>;

/* QC DOCS */
export const Form1098SaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: Form1098Document,
  qcDocumentId: zodBrandedUuid<QcDocumentId>(),
  isCorrected: z.boolean(),
  year: Form1098Year,
  copy: LoanDocumentForm1098Copy,
});
export type Form1098SaveDocumentPdfEvent = z.infer<typeof Form1098SaveDocumentPdfEvent>;

export const EscrowAnalysisSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: EscrowAnalysisDocument,
  qcDocumentId: zodBrandedUuid<QcDocumentId>(),
  effectiveDate: zodDateOrString,
  isCorrected: z.boolean(),
});
export type EscrowAnalysisSaveDocumentPdfEvent = z.infer<typeof EscrowAnalysisSaveDocumentPdfEvent>;

export const DelinquentInterestAppliedSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: DelinquentInterestAppliedDocument,
  paymentConfigId: zodBrandedUuid<PaymentConfigId>().optional(), // TODO remove after migration
  delinquentInterestSettingId: zodBrandedUuid<DelinquentInterestSettingId>().optional(), // Use this!
});

export type DelinquentInterestAppliedSaveDocumentPdfEvent = z.infer<
  typeof DelinquentInterestAppliedSaveDocumentPdfEvent
>;

export const PrincipalModificationAppliedSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PrincipalModificationAppliedDocument,
  principalModificationId: zodBrandedUuid<PrincipalModificationId>(),
});

export type PrincipalModificationAppliedSaveDocumentPdfEvent = z.infer<
  typeof PrincipalModificationAppliedSaveDocumentPdfEvent
>;

export const PrincipalModificationCancelledSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PrincipalModificationCancelledDocument,
  principalModificationId: zodBrandedUuid<PrincipalModificationId>(),
});

export type PrincipalModificationCancelledSaveDocumentPdfEvent = z.infer<
  typeof PrincipalModificationCancelledSaveDocumentPdfEvent
>;

export const MortgageInsuranceCancellationNoticeSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: MortgageInsuranceCancellationNoticeDocument,
  escrowSettingId: zodBrandedUuid<EscrowSettingId>(),
});
export type MortgageInsuranceCancellationNoticeSaveDocumentPdfEvent = z.infer<
  typeof MortgageInsuranceCancellationNoticeSaveDocumentPdfEvent
>;

/* WORKOUT PLAN DOCUMENTS */
export const DefermentWorkoutPlanSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: DefermentWorkoutPlanDocument,
  borrowerIds: z.array(zodBrandedUuid<BorrowerId>()).optional(),
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});

export type DefermentWorkoutPlanSaveDocumentPdfEvent = z.infer<typeof DefermentWorkoutPlanSaveDocumentPdfEvent>;

export const FlexModCancellationNoticeSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: FlexModCancellationNoticeDocument,
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});
export type FlexModCancellationNoticeSaveDocumentPdfEvent = z.infer<
  typeof FlexModCancellationNoticeSaveDocumentPdfEvent
>;

export const ForbearanceWorkoutPlanSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: ForbearanceWorkoutPlanCompletedDocument,
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});
export type ForbearanceWorkoutPlanSaveDocumentPdfEvent = z.infer<typeof ForbearanceWorkoutPlanSaveDocumentPdfEvent>;

export const WorkoutPlanSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: z.union([
    ForbearanceWorkoutPlanEarlyTerminationNoticeDocument,
    RepaymentPlanCancellationNoticeDocument,
  ]),
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});
export type WorkoutPlanSaveDocumentPdfEvent = z.infer<typeof WorkoutPlanSaveDocumentPdfEvent>;

export const RepaymentPlanOfferNoticeSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: RepaymentPlanOfferNoticeDocument,
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
  borrowerIds: z.array(zodBrandedUuid<BorrowerId>()).optional(),
});
export type RepaymentPlanOfferNoticeSaveDocumentPdfEvent = z.infer<typeof RepaymentPlanOfferNoticeSaveDocumentPdfEvent>;

export const PaymentDeferralOfferLetterSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PaymentDeferralOfferLetterDocument,
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});
export type PaymentDeferralOfferLetterSaveDocumentPdfEvent = z.infer<
  typeof PaymentDeferralOfferLetterSaveDocumentPdfEvent
>;

export const PaymentDeferralCancellationNoticeSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: PaymentDeferralCancellationNoticeDocument,
  workoutPlanId: zodBrandedUuid<WorkoutPlanId>(),
});
export type PaymentDeferralCancellationNoticeSaveDocumentPdfEvent = z.infer<
  typeof PaymentDeferralCancellationNoticeSaveDocumentPdfEvent
>;

/* EMAIL COPY TEMPLATES */
const GoodbyeSaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: GoodbyeLetterDocument, // This event already existed, but shouldnt be used anymore
});

export const EmailCopySaveDocumentPdfEvent = BaseSaveDocumentPdfEvent.extend({
  documentType: z.literal('emailCopy'),
  emailTemplateType: EmailTemplateType.optional(), // TODO require after migration
  emailTitle: z.string().optional(), // TODO require after migration
});
export type EmailCopySaveDocumentPdfEvent = z.infer<typeof EmailCopySaveDocumentPdfEvent>;

/* --------------------- */
export const SaveDocumentPdfEvent = z.discriminatedUnion('documentType', [
  PreActiveWelcomeLetterSaveDocumentPdfEvent,
  MortgageeLetterSaveDocumentPdfEvent,
  PayoffStatementSaveDocumentPdfEvent,
  PaidInFullDocumentPdfEvent,
  PartialPayoffConfirmationDocumentPdfEvent,
  LenderUploadSaveDocumentPdfEvent,
  BorrowerUploadSaveDocumentPdfEvent,
  Form1098SaveDocumentPdfEvent,
  EscrowAnalysisSaveDocumentPdfEvent,
  DelinquentInterestAppliedSaveDocumentPdfEvent,
  PrincipalModificationAppliedSaveDocumentPdfEvent,
  PrincipalModificationCancelledSaveDocumentPdfEvent,
  GoodbyeSaveDocumentPdfEvent,
  VerificationOfMortgageSaveDocumentPdfEvent,
  LoanExtensionAgreementSaveDocumentPdfEvent,
  LoanExtensionFeesSaveDocumentPdfEvent,
  EmailCopySaveDocumentPdfEvent,
  MortgageInsuranceCancellationNoticeSaveDocumentPdfEvent,

  // workout plan documents
  DefermentWorkoutPlanSaveDocumentPdfEvent,
  FlexModCancellationNoticeSaveDocumentPdfEvent,
  ForbearanceWorkoutPlanSaveDocumentPdfEvent,
  WorkoutPlanSaveDocumentPdfEvent,
  RepaymentPlanOfferNoticeSaveDocumentPdfEvent,
  PaymentDeferralOfferLetterSaveDocumentPdfEvent,
  PaymentDeferralCancellationNoticeSaveDocumentPdfEvent,
]);
export type SaveDocumentPdfEvent = z.infer<typeof SaveDocumentPdfEvent>;
